import DOMPurify from "dompurify";

/**
 * Sanitize inbound Gmail HTML before rendering via dangerouslySetInnerHTML.
 *
 * Two passes:
 *   1) DOMPurify with an explicit deny-list for XSS vectors + a URI scheme
 *      allowlist (so javascript:, data:text/html, vbscript:, etc. are dropped
 *      from href / src attributes).
 *   2) Image-cleanup regex (preserved from the previous implementation) to
 *      strip cid: imgs, broken-src imgs, and 1x1 tracking pixels. These are
 *      not XSS vectors so DOMPurify won't touch them, but they break inbox
 *      rendering and need to go.
 *
 * Replaces a previous regex-only "sanitizer" that did not block <script>,
 * <iframe>, on* event handlers, or javascript: URIs (Wave 2 audit 2026-05-05,
 * ticket 599dba7d).
 */
export function sanitizeEmailHtml(html: string): string {
  const purified = DOMPurify.sanitize(html, {
    FORBID_TAGS: ["script", "iframe", "object", "embed"],
    FORBID_ATTR: ["onerror", "onclick", "onload", "onmouseover"],
    ALLOWED_URI_REGEXP:
      /^(?:(?:https?|mailto|tel|cid):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  });

  return purified
    // Strip cid: images that weren't converted to data URIs (legacy emails before inline embedding)
    .replace(/<img[^>]*src=["']cid:[^"']*["'][^>]*\/?>/gi, "")
    // Strip images with empty/broken src (but keep data: URIs and http(s) URLs)
    .replace(/<img[^>]*src=["'](?!data:|https?:\/\/)[^"']{0,10}["'][^>]*\/?>/gi, "")
    // Strip 1x1 tracking pixels
    .replace(/<img[^>]*width=["']1["'][^>]*height=["']1["'][^>]*\/?>/gi, "")
    .replace(/<img[^>]*height=["']1["'][^>]*width=["']1["'][^>]*\/?>/gi, "");
}
